import type { ReactNode } from 'react'
import { format } from 'date-fns'
import type { CalendarLeave, WhoIsOutItem } from '@lms/shared-types'
import { displayName } from '../../lib/display-name'
import { Card } from './Card'
import { LeaveBar } from './LeaveBar'
import { WeekBarOverlay } from './WeekBarOverlay'
import type { WeekBarSegment } from './WeekBarOverlay'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function isoOf(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

/** Monday of the *current* working week. When today is Sat or Sun, advance
 *  to next Monday — a manager checking on the weekend wants to see who's
 *  out in the upcoming week, not the already-finished one. */
function weekStart(today: Date): Date {
  const d = new Date(today)
  d.setHours(0, 0, 0, 0)
  const day = d.getDay() // 0=Sun, 1=Mon, ..., 6=Sat
  if (day === 0) {
    // Sunday → next Monday is +1 day.
    d.setDate(d.getDate() + 1)
  } else if (day === 6) {
    // Saturday → next Monday is +2 days.
    d.setDate(d.getDate() + 2)
  } else {
    // Mon–Fri: anchor to the Monday of this week.
    d.setDate(d.getDate() - (day - 1))
  }
  return d
}

interface DayCol {
  iso: string
  short: string
  num: number
  isWeekend: boolean
  isToday: boolean
}

export interface OutThisWeekStripProps {
  items: ReadonlyArray<WhoIsOutItem>
  /** Override "today" for tests. Defaults to the current date. */
  today?: Date
  /** Card heading. Defaults to "Out this week". */
  title?: string
  /**
   * Override the default subtitle ("{N} {person|people} · week of {date}").
   * Pass `null` to hide the subtitle entirely. */
  subtitle?: string | null
  /** Empty-state copy. Defaults to "Everyone in. Full team this week." */
  emptyMessage?: string
}

/**
 * "Out this week" calendar strip — wireframe-aligned port of
 * `new_reference/surface-manager.jsx::OutThisWeekStrip`. Days run Mon → Sun
 * as columns; each person who's out renders as a horizontal bar pinned to
 * the days they're actually out. Bars stack into rows greedily so multiple
 * people on overlapping days don't collide. Today is highlighted, weekend
 * cells are muted.
 *
 * Bar layout is delegated to WeekBarOverlay; only the day-header row,
 * segment building, and domain-specific bar renderer live here.
 */
export function OutThisWeekStrip({
  items,
  today = new Date(),
  title = 'Out this week',
  subtitle,
  emptyMessage = 'Everyone in. Full team this week.',
}: OutThisWeekStripProps): JSX.Element {
  const start = weekStart(today)
  const todayIso = isoOf(today)

  const days: DayCol[] = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return {
      iso: isoOf(d),
      short: DAY_LABELS[i] ?? '',
      num: d.getDate(),
      isWeekend: i >= 5,
      isToday: isoOf(d) === todayIso,
    }
  })
  const weekMin = days[0]!.iso
  const weekMax = days[6]!.iso

  const bars: WeekBarSegment<WhoIsOutItem>[] = []
  for (const item of items) {
    const s = item.startDate > weekMin ? item.startDate : weekMin
    const e = item.endDate < weekMax ? item.endDate : weekMax
    if (s > e) continue
    const startCol = days.findIndex((d) => d.iso === s)
    const endCol = days.findIndex((d) => d.iso === e)
    if (startCol < 0 || endCol < 0) continue
    bars.push({
      id: `${item.employeeId}-${item.startDate}`,
      startCol,
      endCol,
      data: item,
    })
  }

  const peopleCount = new Set(items.map((i) => i.employeeId)).size
  const cols = days.length

  return (
    <Card padding="14px 16px" style={{ marginBottom: 20 }}>
      <div
        data-testid="who-is-out"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 10,
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          {title}
        </h2>
        {subtitle === null ? null : (
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {subtitle ?? `${peopleCount} ${peopleCount === 1 ? 'person' : 'people'} · week of ${format(start, 'dd MMM')}`}
          </span>
        )}
      </div>

      {items.length === 0 ? (
        <div
          data-testid="who-is-out-empty"
          style={{ fontSize: 13, color: 'var(--text-3)', padding: '8px 0' }}
        >
          {emptyMessage}
        </div>
      ) : (
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              borderBottom: '1px solid var(--border)',
              marginBottom: 8,
            }}
          >
            {days.map((day, i) => (
              <div
                key={day.iso}
                style={{
                  padding: '6px 4px 8px',
                  textAlign: 'center',
                  borderRight:
                    i < cols - 1 ? '1px solid var(--french-gray-2)' : 'none',
                  background: day.isWeekend
                    ? 'var(--surface-2)'
                    : 'transparent',
                  opacity: day.isWeekend ? 0.7 : 1,
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-mono, inherit)',
                    fontSize: 9,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--text-3)',
                    marginBottom: 2,
                  }}
                >
                  {day.short}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-mono, inherit)',
                    fontSize: 13,
                    fontWeight: day.isToday ? 700 : 500,
                    color: day.isToday ? 'var(--surface)' : 'var(--text)',
                    background: day.isToday ? 'var(--text)' : 'transparent',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                  }}
                >
                  {day.num}
                </div>
              </div>
            ))}
          </div>

          {/* Weekend column tint sits behind the bar overlay as a fixed grid. */}
          <div style={{ position: 'relative' }}>
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                pointerEvents: 'none',
              }}
            >
              {days.map((day, ci) => (
                <div
                  key={day.iso}
                  style={{
                    borderRight:
                      ci < cols - 1 ? '1px solid var(--french-gray-2)' : 'none',
                    background: day.isWeekend
                      ? 'var(--surface-2)'
                      : 'transparent',
                    opacity: day.isWeekend ? 0.5 : 1,
                  }}
                />
              ))}
            </div>
            <WeekBarOverlay
              bars={bars}
              cols={cols}
              maxRows={99}
              barHeight={24}
              rowGap={4}
              renderBar={({ data }) => renderPersonBar(data)}
            />
          </div>
        </div>
      )}
    </Card>
  )
}

function renderPersonBar(item: WhoIsOutItem): ReactNode {
  const leaveForBar: CalendarLeave = {
    id: `${item.employeeId}-${item.startDate}`,
    employeeId: item.employeeId,
    employeeName: displayName({ name: item.employeeName, preferredName: item.employeePreferredName }),
    teamId: item.teamId,
    teamName: item.teamName,
    type: item.leaveType,
    startDate: item.startDate,
    endDate: item.endDate,
    halfDay: false,
    reason: item.reason ?? null,
    status: 'approved',
  }
  return (
    <div
      data-testid="out-row"
      style={{ position: 'absolute', inset: 0, paddingLeft: 2, paddingRight: 2 }}
    >
      <LeaveBar
        leave={leaveForBar}
        startsHere
        endsHere
        variant="compact"
        density="low"
      />
    </div>
  )
}
